import { EmbeddedCode } from '../shared/code';
import { Lex, LexoRank } from '../shared/lexorank';
import { JobTemplateCode } from './job.template.workorder';
import { Entity } from '../entity';
import { OpportunityStage } from '../opportunities/opportunity.stage';

export enum JobTemplateQuestionType {
    Text = 'Text',
    Numeric = 'Numeric',
    Date = 'Date',
    Attachment = 'Attachment',
    Dropdown = 'Dropdown',
    Boolean = 'Boolean',
}

function setSupportedActions(defaultAction: boolean, actions?: Partial<JobTemplateQuestionAllowedActions>): JobTemplateQuestionAllowedActions {
    return {
        addItems: defaultAction,
        appendToJobDescription: defaultAction,
        setTag: defaultAction,
        setJobType: defaultAction,
        setPriority: defaultAction,
        setPriceList: defaultAction,
        setFieldSupervisor: defaultAction,
        setCustomFields: defaultAction,
        setOwner: defaultAction,
        setFiles: defaultAction,
        ...actions
    };
}

export function newJobTemplateQuestion(type: JobTemplateQuestionType, name: string): JobTemplateQuestion {

    const defaultQuestion = {
        _id: null,
        name: name,
        description: null,
        type: type,
        options: [],
        multiple: false,
        sortOrder: LexoRank.first()
    };

    switch (type) {
        case JobTemplateQuestionType.Boolean: {
            return {
                ...defaultQuestion,
                options: [
                    { _id: null, name: 'Yes', sortOrder: LexoRank.first(), system: true },
                    { _id: null, name: 'No', sortOrder: LexoRank.from(LexoRank.first()).increment().value, system: true }
                ]
            }
            break;
        }
        case JobTemplateQuestionType.Date: {
            return {
                ...defaultQuestion,
                options: [
                    { _id: null, name: 'Default', sortOrder: LexoRank.first(), system: true, hidden: true }
                ]
            }
            break;
        }
        case JobTemplateQuestionType.Numeric: {
            return {
                ...defaultQuestion,
                options: [
                    { _id: null, name: 'Default', sortOrder: LexoRank.first(), system: true, hidden: false, setLimit: true, min: 0 }
                ]
            }
            break;
        }
        case JobTemplateQuestionType.Text: {
            return {
                ...defaultQuestion,
                options: [
                    { _id: null, name: 'Default', sortOrder: LexoRank.first(), system: true, hidden: true, setLimit: true, min: 0 }
                ]
            }
            break;
        }
        case JobTemplateQuestionType.Dropdown: {
            return {
                ...defaultQuestion,
                options: [
                    { _id: null, name: 'Option 1', sortOrder: LexoRank.first(), system: true },
                ]
            }
            break;
        }
    }
}

export const JobTemplateQuestionTypeAllowedActions = {
    [JobTemplateQuestionType.Attachment]: setSupportedActions(false, { setFiles: true }),
    [JobTemplateQuestionType.Boolean]: setSupportedActions(true, { setOpportunityStage: true, setOpportunitySource: true, }),
    [JobTemplateQuestionType.Date]: setSupportedActions(false, { appendToJobDescription: true, setCustomFields: true, setOpportunityExpectedCloseDate: true }),
    [JobTemplateQuestionType.Dropdown]: setSupportedActions(true, { setOpportunityStage: true, setOpportunitySource: true, }),
    [JobTemplateQuestionType.Numeric]: setSupportedActions(false, { appendToJobDescription: true, setCustomFields: true, addItems: true, setOpportunityEstimatedAmount: true }),
    [JobTemplateQuestionType.Text]: setSupportedActions(false, { appendToJobDescription: true, setCustomFields: true }),
}

export interface JobTemplateQuestionAllowedActions {
    addItems?: boolean;
    appendToJobDescription?: boolean;
    setTag?: boolean;
    setJobType?: boolean;
    setPriority?: boolean;
    setPriceList?: boolean;
    setFieldSupervisor?: boolean;
    setCustomFields?: boolean;
    setOwner?: boolean;
    setFiles?: boolean;

    setOpportunityStage?: boolean;
    setOpportunitySource?: boolean;
    setOpportunityEstimatedAmount?: boolean;
    setOpportunityExpectedCloseDate?: boolean;
}

export type JobTemplateCodeLike = JobTemplateCode | EmbeddedCode;
export interface JobTemplateQuestionOption extends Entity {
    _id: string;
    name: string;
    description?: string;
    system?: boolean;
    hidden?: boolean;
    sortOrder: Lex;

    setLimit?: boolean;
    min?: number;
    max?: number;

    appendToJobDescription?: boolean;
    jobDescription?: string;
    jobType?: Entity;
    priority?: Entity;
    priceList?: Entity;
    fieldSupervisor?: Entity;
    owner?: Entity;
    tags?: Entity[];
    addToDuration?: number;
    items?: JobTemplateCodeLike[];
    customFields?: Record<string, any>;
    setCustomField?: string;

    opportunityStage?: OpportunityStage;
    opportunitySource?: Entity;
    opportunityEstimatedAmount?: number;
    opportunityExpectedCloseDate?: Date;
}

export interface JobTemplateQuestionVisibilityCondition {
    question: string;
    value: string;
}

export interface JobTemplateQuestion extends Entity {
    _id: string;
    name: string;
    description: string;
    type: JobTemplateQuestionType;
    options?: JobTemplateQuestionOption[];
    multiple?: boolean;
    sortOrder: Lex;
    showCondition?: JobTemplateQuestionVisibilityCondition;
}

export interface JobTemplateQuestionAnswer {
    question: JobTemplateQuestion;
    value: any;
    passedCondition?: boolean;
}
